// src/components/StatsSection.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaBuilding, 
  FaUsers, 
  FaStar, 
  FaCalendarAlt,
  FaCoffee,
  FaSmile,
  FaTrophy,
  FaClock
} from 'react-icons/fa';

const StatsSection = () => {
  const [imageError, setImageError] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Aapki background image
  const backgroundImage = "https://res.cloudinary.com/dgwnm39m7/image/upload/v1772789151/StatsSectionImage_anboqi.jpg";

  // Stats data
  const stats = [
    {
      icon: <FaBuilding />,
      number: "50+",
      label: "Luxury Apartments",
      description: "Premium serviced residences",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <FaUsers />,
      number: "1000+",
      label: "Happy Guests",
      description: "Satisfied customers and counting",
      color: "from-green-500 to-green-600"
    },
    {
      icon: <FaStar />,
      number: "4.9",
      label: "Guest Rating",
      description: "Average rating across all platforms",
      color: "from-yellow-500 to-yellow-600"
    },
    {
      icon: <FaCalendarAlt />,
      number: "2024",
      label: "Established",
      description: "Serving since our founding year",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: <FaCoffee />,
      number: "50+",
      label: "Nearby Cafés",
      description: "Dining options at your doorstep",
      color: "from-orange-500 to-orange-600"
    },
    {
      icon: <FaSmile />,
      number: "98%",
      label: "Return Rate",
      description: "Guests who come back to stay",
      color: "from-pink-500 to-pink-600"
    },
    {
      icon: <FaTrophy />,
      number: "5",
      label: "Awards Won",
      description: "Industry recognition & accolades",
      color: "from-amber-500 to-amber-600"
    },
    {
      icon: <FaClock />,
      number: "24/",
      label: "Concierge",
      description: "Round-the-clock assistance",
      color: "from-red-500 to-red-600"
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 12,
        duration: 0.6 
      }
    },
  };

  // Counter animation component
  const Counter = ({ value, duration = 2000 }) => {
    const [count, setCount] = React.useState(0);
    const [counterRef, counterInView] = useInView({ triggerOnce: true });

    React.useEffect(() => {
      if (counterInView) {
        let start = 0;
        const end = parseInt(value.replace(/[^0-9]/g, ''));
        const increment = end / (duration / 16);
        
        const timer = setInterval(() => {
          start += increment;
          if (start >= end) {
            setCount(end);
            clearInterval(timer);
          } else {
            setCount(Math.floor(start));
          }
        }, 16);

        return () => clearInterval(timer);
      }
    }, [counterInView, value, duration]);

    return (
      <span ref={counterRef} className="counter-value">
        {count}
        {value.includes('+') && '+'}
        {value.includes('%') && '%'}
        {value.includes('.') && !value.includes('+') && !value.includes('%') && '.9'}
        {value.includes('/') && '/7'}
      </span>
    );
  };

  return (
    <section className="relative py-12 overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 backgroundattachment bg-cover bg-no-repeat"
        style={{
          backgroundImage: !imageError ? `url('${backgroundImage}')` : 'none',backgroundPosition:"50% 50%",
          backgroundColor: !imageError ? 'transparent' : '#1a2b3c', // fallback color
        }}
        onError={() => setImageError(true)}
      >
        {/* Dark Overlay for text readability */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" 
            style={{
              backgroundImage: `radial-gradient(circle at 20px 20px, #c9a86c 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>
      </div>

      {/* Fallback gradient if image fails */}
      {imageError && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2b3c] to-[#0f1a24]">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full" 
              style={{
                backgroundImage: `radial-gradient(circle at 20px 20px, #c9a86c 1px, transparent 1px)`,
                backgroundSize: '40px 40px'
              }}
            />
          </div>
        </div>
      )}

      {/* Floating Orbs for depth */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#c9a86c]/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-[#c9a86c]/10 rounded-full blur-3xl"></div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-8 space-y-2"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
            <span className="text-[#c9a86c] font-medium tracking-[0.2em] text-xs uppercase">
              By The Numbers
            </span>
            <div className="w-12 h-[2px] bg-[#c9a86c]"></div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-light text-white !mb-4">
            Our{' '}
            <span className="font-bold text-[#c9a86c]">Achievements</span>{' '}
            in Numbers
          </h2>
          
          <p className="text-gray-300 md:text-[17px] 2xl:text-lg leading-relaxed">
            Trusted by hundreds of guests, recognized by industry leaders, and committed to excellence every day.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ 
                y: -5,
                transition: { duration: 0.2 }
              }}
              className="group p-8 rounded-2xl bg-transparent shadow-lg hover:shadow-xl border border-white/60 hover:border-white/70 transition-all duration-300"
            >
              {/* Icon with Gradient */}
              
                <div className="w-14 h-14 bg-[#c9a86c]/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-[#c9a86c] transition">
                    <div className="text-[#c9a86c] text-xl group-hover:text-white transition" >{stat.icon}</div>
                  </div>
                

              {/* Number with Counter */}
              <div className="text-3xl font-bold text-white mb-2">
                {stat.number.includes('+') || stat.number.includes('%') || stat.number.includes('/') ? (
                  <Counter value={stat.number} />
                ) : (
                  stat.number
                )}
              </div>

              {/* Label */}
              <h3 className="text-md font-semibold text-[#c9a86c] mb-3">
                {stat.label}
              </h3>

              {/* Description */}
              <p className="2xl:text-lg text-gray-300 leading-relaxed">
                {stat.description}
              </p>

              {/* Decorative Corner
              <div className="absolute top-4 right-4 w-12 h-12 opacity-0 group-hover:opacity-20 transition-opacity duration-300">
                <div className={`w-full h-full bg-gradient-to-br ${stat.color} rounded-full`}></div>
              </div> */}
            </motion.div>
          ))}
        </motion.div>

        {/* Trust Badges
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex flex-wrap justify-center gap-8 mt-16 pt-8 border-t border-white/20"
        >
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
            <span className="text-sm">Verified by Google</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
            <span className="text-sm">TripAdvisor Recommended</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-2 h-2 bg-[#c9a86c] rounded-full"></div>
            <span className="text-sm">Booking.com 9.5 Rating</span>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default StatsSection;